"use client";

import React, { useState } from "react";
import { Droplets, ShieldCheck, Flame, Layers, Gauge, ArrowRight, CircleCheck as CheckCircle2, SlidersHorizontal as Sliders, FileText, Activity, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { type L6 } from "@/lib/commonCopy";
interface SolutionItem {
  id: string;
  icon: React.ElementType;
  title: L6;
  category: L6;
  description: L6;
  alloys: string[];
  apertureRange: string;
  keyBenefit: L6;
  metrics: {
    label: L6;
    value: string;
  }[];
}
const solutionsList: SolutionItem[] = [{
  id: "wellhead-sand",
  icon: ShieldCheck,
  title: {
    en: "Wellhead Sand Control & Particulate Screens",
    zh: "井口防砂与固体颗粒截留筛网",
    es: "Cribas de control de arena y partículas en cabeza de pozo",
    de: "Bohrlochkopf-Sandschutz- & Partikelsiebe",
    fr: "Tamis de contrôle du sable et des particules en tête de puits",
    pt: "Telas de controle de areia e partículas em cabeça de poço"
  },
  category: {
    en: "Upstream Extraction",
    zh: "上游开采",
    es: "Extracción upstream",
    de: "Upstream-Förderung",
    fr: "Extraction en amont",
    pt: "Extração upstream"
  },
  description: {
    en: "Heavy-duty Dutch weave wire mesh and slotted wedge wire assemblies engineered to withstand extreme hydrodynamic velocities, preventing particulate ingress and pump erosion.",
    zh: "重型荷兰编织丝网与梯形缝隙楔形丝总成，专为承受严苛的水动力流速冲击而设计，有效阻断微粒侵入并防止井下泵体气蚀冲刷磨损。",
    es: "Mallas tejidas Dutch para servicio pesado y conjuntos de alambre en cuña diseñados para soportar velocidades hidrodinámicas extremas, evitando el ingreso de partículas y la erosión de bombas.",
    de: "Hochbelastbares Tressengewebe und Spaltsiebbaugruppen, die extremen hydrodynamischen Geschwindigkeiten standhalten und Partikeleintrag sowie Pumpenerosion verhindern.",
    fr: "Toiles métalliques néerlandaises renforcées et ensembles à fils profilés conçus pour résister aux vitesses hydrodynamiques extrêmes, empêchant l'intrusion de particules et l'érosion des pompes.",
    pt: "Malhas tecidas Dutch para serviço pesado e montagens de arame cunha projetadas para suportar velocidades hidrodinâmicas extremas, prevenindo o ingresso de partículas e a erosão de bombas."
  },
  alloys: ["316L Stainless", "Super Duplex 2507", "Alloy 625"],
  apertureRange: "20 µm – 500 µm",
  keyBenefit: {
    en: "Extreme burst pressure resistance up to 350 bar differential",
    zh: "最高可承受 350 bar 极限压差爆破强度",
    es: "Resistencia extrema a la rotura por presión diferencial de hasta 350 bar",
    de: "Extreme Berstdruckfestigkeit bis zu 350 bar Differenzdruck",
    fr: "Résistance extrême à l'éclatement jusqu'à 350 bar de pression différentielle",
    pt: "Resistência extrema ao rompimento por pressão diferencial de até 350 bar"
  },
  metrics: [{
    label: {
      en: "Max Differential Pressure",
      zh: "最大耐受工作压差",
      es: "Presión diferencial máx.",
      de: "Max. Differenzdruck",
      fr: "Pression différentielle max.",
      pt: "Pressão diferencial máx."
    },
    value: "350 Bar"
  }, {
    label: {
      en: "Standard Weave",
      zh: "常用编织与型材结构",
      es: "Tipo de tejido estándar",
      de: "Standard-Gewebeart",
      fr: "Tissage standard",
      pt: "Trama padrão"
    },
    value: "Twill Dutch / Wedge"
  }]
}, {
  id: "amine-glycol",
  icon: Droplets,
  title: {
    en: "Amine & Glycol Gas Sweetening Filters",
    zh: "胺液与乙二醇天然气脱硫脱水滤芯",
    es: "Filtros de endulzamiento de gas con amina y glicol",
    de: "Amin- & Glykol-Gasentschwefelungsfilter",
    fr: "Filtres de désacidification du gaz aux amines et au glycol",
    pt: "Filtros de desacidificação de gás por amina e glicol"
  },
  category: {
    en: "Midstream Processing",
    zh: "中游储运加工",
    es: "Procesamiento midstream",
    de: "Midstream-Verarbeitung",
    fr: "Traitement midstream",
    pt: "Processamento midstream"
  },
  description: {
    en: "Multi-layered sintered mesh element packs designed to capture micro-iron sulfides and hydrocarbon aerosols, eliminating foam formation and reboiler fouling in gas treatment units.",
    zh: "多层烧结金属网滤芯组件，专门用于捕集微米级硫化铁颗粒与重烃类气溶胶，防止气体脱硫脱水塔发泡及重沸器结垢堵塞。",
    es: "Paquetes de malla sinterizada multicapa diseñados para capturar sulfuros de hierro microscópicos y aerosoles de hidrocarburos, evitando la formación de espuma y el ensuciamiento del hervidor.",
    de: "Mehrlagige Sintergewebe-Elemente zur Abscheidung von Mikro-Eisensulfiden und Kohlenwasserstoff-Aerosolen, die Schaumbildung und Fouling in Verdampfern verhindern.",
    fr: "Packs de toiles frittées multicouches conçus pour capturer les sulfures de fer micrométriques et les aérosols d'hydrocarbures, éliminant la formation de mousse et l'encrassement des rebouilleurs.",
    pt: "Pacotes de malha sinterizada multicamada projetados para reter sulfetos de ferro microscópicos e aerossóis de hidrocarbonetos, evitando a formação de espuma e a incrustação de refervedores."
  },
  alloys: ["316L Stainless", "304L Stainless", "Monel 400"],
  apertureRange: "5 µm – 50 µm",
  keyBenefit: {
    en: "High beta ratio efficiency for iron sulfide particulates",
    zh: "针对硫化铁微粒具备高 Beta 比截留效率",
    es: "Alta eficiencia de relación beta para partículas de sulfuro de hierro",
    de: "Hohe Beta-Verhältnis-Effizienz für Eisensulfid-Partikel",
    fr: "Efficacité élevée du rapport bêta pour les particules de sulfure de fer",
    pt: "Alta eficiência da relação beta para partículas de sulfeto de ferro"
  },
  metrics: [{
    label: {
      en: "Particulate Removal",
      zh: "颗粒截留指标",
      es: "Eliminación de partículas",
      de: "Partikelabscheidung",
      fr: "Élimination des particules",
      pt: "Remoção de partículas"
    },
    value: "Beta 5000"
  }, {
    label: {
      en: "Operating Temp",
      zh: "额定工作温度",
      es: "Temperatura de operación",
      de: "Betriebstemperatur",
      fr: "Température de service",
      pt: "Temperatura de operação"
    },
    value: "Up to 300°C"
  }]
}, {
  id: "produced-water",
  icon: Layers,
  title: {
    en: "Produced Water & Oil-In-Water Separators",
    zh: "油田采出水与油水分离聚结过滤网",
    es: "Separadores de agua de producción y aceite en agua",
    de: "Lagerstättenwasser- & Öl-in-Wasser-Abscheider",
    fr: "Séparateurs d'eau de production et huile-dans-eau",
    pt: "Separadores de água produzida e óleo na água"
  },
  category: {
    en: "Water Management",
    zh: "采出水循环管理",
    es: "Gestión de aguas",
    de: "Wassermanagement",
    fr: "Gestion de l'eau",
    pt: "Gestão hídrica"
  },
  description: {
    en: "High-flow corrosion-resistant wire mesh matrices and coalescing filter elements engineered for produced water reinjection and offshore environmental discharge compliance.",
    zh: "大流量耐腐蚀不锈钢金属丝网骨架及油水分离聚结滤芯，专为油田注水回注地层净化及海上平台环保达标外排处理开发。",
    es: "Matrices de malla de alambre resistentes a la corrosión de alto caudal y elementos coalescentes diseñados para reinyección de agua producida y descarga ambiental en alta mar.",
    de: "Hochdurchlässige, korrosionsbeständige Drahtgewebematrizen und Koaleszenzfilter für die Reinjektion von Lagerstättenwasser und Offshore-Einleitung.",
    fr: "Matrices de toiles métalliques haute perméabilité résistantes à la corrosion et éléments coalescents pour la réinjection des eaux de production et le rejet en mer.",
    pt: "Matrizes de tela metálica de alta vazão resistentes à corrosão e elementos coalescentes desenvolvidos para reinjeção de água produzida e descarte ambiental offshore."
  },
  alloys: ["2205 Duplex", "Hastelloy C-276", "316L Stainless"],
  apertureRange: "10 µm – 250 µm",
  keyBenefit: {
    en: "High flow rate maintenance with absolute zero media migration",
    zh: "保持超大流体通量且滤料零介质脱落释放",
    es: "Mantenimiento de alto caudal sin migración de material filtrante",
    de: "Hoher Durchfluss bei absolut null Filtermedium-Migration",
    fr: "Maintien d'un débit élevé sans aucune migration du média filtrant",
    pt: "Manutenção de alta vazão com migração zero de meio filtrante"
  },
  metrics: [{
    label: {
      en: "Flow Capacity",
      zh: "流体通透能力",
      es: "Capacidad de flujo",
      de: "Durchflusskapazität",
      fr: "Capacité de débit",
      pt: "Capacidade de vazão"
    },
    value: "High Permeability"
  }, {
    label: {
      en: "Salinity Resistance",
      zh: "高盐水环境耐蚀",
      es: "Resistencia a la salinidad",
      de: "Salzgehaltbeständigkeit",
      fr: "Résistance à la salinité",
      pt: "Resistência à salinidade"
    },
    value: "Subsea Qualified"
  }]
}, {
  id: "refinery-catalyst",
  icon: Flame,
  title: {
    en: "Refinery Catalyst Support & Retention Beds",
    zh: "炼油催化剂床层支承网与捕集屏障",
    es: "Lechos de soporte y retención de catalizador para refinería",
    de: "Raffinerie-Katalysatorträger- & Rückhalteböden",
    fr: "Lits de support et de rétention de catalyseur de raffinerie",
    pt: "Leitos de suporte e retenção de catalisador para refinaria"
  },
  category: {
    en: "Downstream Refining",
    zh: "下游炼化装置",
    es: "Refino downstream",
    de: "Downstream-Raffination",
    fr: "Raffinage en aval",
    pt: "Refino downstream"
  },
  description: {
    en: "Precision woven metal wire cloth and high-temperature support grids engineered for hydrotreating, catalytic cracking (FCCU), and alkylation reactor vessels.",
    zh: "高精度编织金属丝网与耐高温结构支撑栅格，专用于加氢精制、重油催化裂化（FCCU）及烷基化反应器床层催化剂颗粒拦截。",
    es: "Telas metálicas de precisión y rejillas de soporte para altas temperaturas diseñadas para reactores de hidrotratamiento, craqueo catalítico (FCCU) y alquilación.",
    de: "Präzisionsdrahtgewebe und Hochtemperatur-Tragroste für Hydrotreating-, FCCU- und Alkylierungsreaktoren.",
    fr: "Toiles métalliques de précision et grilles de support haute température conçues pour les réacteurs d'hydrotraitement, de craquage catalytique (FCCU) et d'alkylation.",
    pt: "Telas metálicas tecidas de precisão e grades de suporte para alta temperatura projetadas para hidrotratamento, craqueamento catalítico (FCCU) e reatores de alquilação."
  },
  alloys: ["310S Stainless", "Inconel 600", "Hastelloy C-276"],
  apertureRange: "50 µm – 1000 µm",
  keyBenefit: {
    en: "Maintains thermal integrity and structural rigidity at extreme temperatures",
    zh: "在持续高温极端热循环工况下保持高机械强度与网孔稳定性",
    es: "Mantiene la integridad térmica y la rigidez estructural a temperaturas extremas",
    de: "Behält thermische Integrität und Struktursteifigkeit bei extremen Temperaturen bei",
    fr: "Conserve l'intégrité thermique et la rigidité structurale à températures extrêmes",
    pt: "Mantém a integridade térmica e a rigidez estrutural sob temperaturas extremas"
  },
  metrics: [{
    label: {
      en: "Temperature Rating",
      zh: "耐受工作温区",
      es: "Rango de temperatura",
      de: "Temperaturbereich",
      fr: "Plage de température",
      pt: "Faixa de temperatura"
    },
    value: "Up to 800°C"
  }, {
    label: {
      en: "ASTM Standard",
      zh: "制造参考标准",
      es: "Norma ASTM",
      de: "ASTM-Norm",
      fr: "Norme ASTM",
      pt: "Norma ASTM"
    },
    value: "ASTM E2016 Compliant"
  }]
}];
interface SpecFeature {
  title: L6;
  desc: L6;
}
const technicalSpecs: SpecFeature[] = [{
  title: {
    en: "Positive Material Identification (PMI)",
    zh: "材质光谱验证 (PMI)",
    es: "Identificación positiva de materiales (PMI)",
    de: "Positive Materialidentifikation (PMI)",
    fr: "Identification positive des matériaux (PMI)",
    pt: "Identificação positiva de materiais (PMI)"
  },
  desc: {
    en: "PMI/XRF alloy verification can be arranged when specified in the order.",
    zh: "可按订单明确要求安排 PMI/XRF 光谱合金成分验证检测。",
    es: "La verificación de aleación PMI/XRF se puede gestionar previa especificación en el pedido.",
    de: "PMI/XRF-Legierungsprüfung kann auf Bestellanfrage vereinbart werden.",
    fr: "Le contrôle d'alliage PMI/XRF peut être organisé sur demande à la commande.",
    pt: "A verificação de liga PMI/XRF pode ser realizada quando especificada no pedido."
  }
}, {
  title: {
    en: "Custom Cut-To-Size & Sintered Packs",
    zh: "定制分切与多层烧结网组",
    es: "Corte a medida y paquetes sinterizados",
    de: "Maßzuschnitt & gesinterte Pakete",
    fr: "Découpe sur mesure et packs frittés",
    pt: "Corte sob medida e pacotes sinterizados"
  },
  desc: {
    en: "Precision laser-cut shapes, wrapped tubes, multi-layer sintered discs, and custom caps.",
    zh: "提供高精度激光下料异型件、卷圆焊筒、多层烧结滤盘及定制端盖连接结构。",
    es: "Formatos de corte láser de precisión, tubos conformados, discos sinterizados multicapa y tapas personalizadas.",
    de: "Präzise lasergeschnittene Formen, gewickelte Rohre, mehrlagige Sinterscheiben und Sonderkappen.",
    fr: "Formes découpées au laser, tubes roulés-soudés, disques frittés multicouches et embouts sur mesure.",
    pt: "Formatos cortados a laser de precisão, tubos calandrados, discos sinterizados multicamada e tampas personalizadas."
  }
}, {
  title: {
    en: "Sour Gas & NACE MR0175 Compliance",
    zh: "酸性气体与 NACE MR0175 合规",
    es: "Conformidad con gas amargo y NACE MR0175",
    de: "Sauergas & NACE MR0175-Konformität",
    fr: "Conformité gaz acide et NACE MR0175",
    pt: "Conformidade com gás ácido e NACE MR0175"
  },
  desc: {
    en: "Corrosion-resistant alloys tested for sulfide stress cracking in high H2S service.",
    zh: "特种耐蚀合金材质可按要求提供硫化氢环境应力腐蚀抗裂性能验证。",
    es: "Aleaciones resistentes a la corrosión evaluadas para agrietamiento por sulfuros en servicio de alto H2S.",
    de: "Korrosionsbeständige Legierungen für den Einsatz bei hoher H2S-Beanspruchung gegen Sulfidspannungsrisskorrosion.",
    fr: "Alliages résistants à la corrosion testés contre la fissuration sous contrainte en présence de H2S.",
    pt: "Ligas resistentes à corrosão avaliadas contra trincas sob tensão por sulfetos em serviços com alto teor de H2S."
  }
}, {
  title: {
    en: "Hydrodynamic Flow Sizing",
    zh: "流体水动力学计算与选型",
    es: "Dimensionamiento de flujo hidrodinámico",
    de: "Hydrodynamische Durchflussauslegung",
    fr: "Dimensionnement des flux hydrodynamiques",
    pt: "Dimensionamento de fluxo hidrodinâmico"
  },
  desc: {
    en: "Engineering team calculates pressure drop curves and micron threshold suitability.",
    zh: "工程技术团队核算流体阻力压降曲线，协助确认最佳孔径精度与有效过滤面积。",
    es: "El equipo de ingeniería calcula curvas de caída de presión y adecuación de micras.",
    de: "Das Engineering-Team berechnet Druckverlustkurven und prüft die Eignung der Trenngrenze.",
    fr: "L'équipe d'ingénierie calcule les courbes de perte de charge et valide les seuils de filtration.",
    pt: "A equipe de engenharia calcula curvas de perda de carga e adequação do grau em micras."
  }
}];
const copy = {
  badgeCategory: {
    en: "Industrial Filtration Engineering",
    zh: "工业过滤工程方案",
    es: "Ingeniería de filtración industrial",
    de: "Industrielle Filtrationstechnik",
    fr: "Ingénierie de filtration industrielle",
    pt: "Engenharia de filtração industrial"
  },
  h2: {
    en: "Oil & Gas Separation Solutions",
    zh: "油气分离核心应用方案",
    es: "Soluciones de separación para petróleo y gas",
    de: "Lösungen für die Öl- und Gastrennung",
    fr: "Solutions de séparation pétrole et gaz",
    pt: "Soluções de separação para óleo e gás"
  },
  lead: {
    en: "Engineered stainless steel, Duplex, and superalloy wire mesh packs, wedge wire screens, and sintered media designed for aggressive hydrocarbon, sour gas, and high-pressure separation applications.",
    zh: "专为严苛烃类介质、酸性气体与高压分离工况量身定制的不锈钢、双相钢与镍基超合金编织网组件、楔形丝筛网及烧结滤材。",
    es: "Paquetes de malla de alambre de acero inoxidable, Duplex y superaleaciones, cribas de alambre en cuña y medios sinterizados para hidrocarburos agresivos, gas amargo y alta presión.",
    de: "Maßgeschneiderte Drahtgewebepakete aus Edelstahl, Duplex und Superlegierungen, Spaltsiebe und Sintermedien für aggressive Kohlenwasserstoffe, Sauergas und Hochdrucktrennung.",
    fr: "Packs de toiles métalliques en inox, Duplex et superalliages, crépines à fil profilé et médias frittés pour hydrocarbures agressifs, gaz acide et séparation haute pression.",
    pt: "Pacotes de tela metálica de aço inoxidável, Duplex e superligas, telas de arame em cunha e meios sinterizados para hidrocarbonetos agressivos, gás ácido e alta pressão."
  },
  selectApp: {
    en: "Select Application",
    zh: "选择工况应用",
    es: "Seleccionar aplicación",
    de: "Anwendung wählen",
    fr: "Sélectionner l'application",
    pt: "Selecionar aplicação"
  },
  apertureRangeLabel: {
    en: "Aperture Range:",
    zh: "孔径公差范围：",
    es: "Rango de abertura:",
    de: "Öffnungsbereich:",
    fr: "Plage d'ouverture :",
    pt: "Faixa de abertura:"
  },
  materialGradesLabel: {
    en: "Material Grade Options",
    zh: "材质合金可选牌号",
    es: "Opciones de grados de material",
    de: "Verfügbare Werkstoffgüten",
    fr: "Nuances de matériaux disponibles",
    pt: "Opções de graus de material"
  },
  engineeringAdvantage: {
    en: "Engineering Advantage",
    zh: "工程结构技术优势",
    es: "Ventaja de ingeniería",
    de: "Technischer Vorteil",
    fr: "Avantage technique",
    pt: "Vantagem de engenharia"
  },
  customSizesNote: {
    en: "Custom pore sizes & dimensions available",
    zh: "支持按需定制孔隙精度与结构几何尺寸",
    es: "Tamaños de poro y dimensiones personalizadas disponibles",
    de: "Individuelle Porenweiten & Abmessungen verfügbar",
    fr: "Tailles de pores et dimensions personnalisées disponibles",
    pt: "Tamanhos de poro e dimensões personalizadas disponíveis"
  },
  requestSizing: {
    en: "Request Sizing Support",
    zh: "申请工程选型支持",
    es: "Solicitar soporte de dimensionamiento",
    de: "Auslegungsunterstützung anfragen",
    fr: "Demander une aide au dimensionnement",
    pt: "Solicitar suporte de dimensionamento"
  },
  ctaEyebrow: {
    en: "Direct Engineering Collaboration",
    zh: "工程技术直连对接",
    es: "Colaboración directa de ingeniería",
    de: "Direkte Zusammenarbeit mit Ingenieuren",
    fr: "Collaboration directe avec nos ingénieurs",
    pt: "Colaboração direta de engenharia"
  },
  ctaTitle: {
    en: "Require Custom Mesh Sizing or Flow Calculations?",
    zh: "需要定制丝网精度核算或流体水动力学计算？",
    es: "¿Requiere dimensionamiento de malla a medida o cálculos de flujo?",
    de: "Benötigen Sie individuelle Gewebeauslegung oder Strömungsberechnungen?",
    fr: "Besoin d'un dimensionnement sur mesure ou de calculs d'écoulement ?",
    pt: "Precisa de dimensionamento de tela sob medida ou cálculos de vazão?"
  },
  ctaBody: {
    en: "Submit your process stream parameters—fluid density, operating temperature, viscosity, and allowable differential pressure—for exact mesh recommendation and sizing support.",
    zh: "欢迎提供工艺流体参数（流体密度、工作温度、介质粘度及允许工作压差），工程团队将为您提供精准的网型推荐与流阻核算方案。",
    es: "Envíe los parámetros de su corriente de proceso (densidad, temperatura, viscosidad y presión diferencial admisible) para una recomendación precisa.",
    de: "Übermitteln Sie Ihre Prozessparameter wie Fluiddichte, Betriebstemperatur, Viskosität und zulässigen Differenzdruck für präzise Auslegungsunterstützung.",
    fr: "Soumettez vos paramètres de procédé (densité du fluide, température, viscosité et perte de charge admissible) pour une recommandation exacte.",
    pt: "Envie os parâmetros do fluxo do processo (densidade do fluido, temperatura, viscosidade e pressão diferencial admissível) para recomendações precisas."
  },
  ctaDatasheets: {
    en: "Technical Datasheets",
    zh: "技术规格表",
    es: "Fichas técnicas",
    de: "Technische Datenblätter",
    fr: "Fiches techniques",
    pt: "Fichas técnicas"
  },
  imgAlt: {
    en: "Stainless steel wire mesh for oil and gas separation",
    zh: "油气分离专用不锈钢过滤筛网",
    es: "Malla de alambre de acero inoxidable para separación de petróleo y gas",
    de: "Edelstahl-Drahtgewebe für die Öl- und Gastrennung",
    fr: "Toile métallique en inox pour la séparation pétrole et gaz",
    pt: "Tela metálica de aço inoxidável para separação de óleo e gás"
  }
};
export default function OilGasSolutions() {
  const {
    lang
  } = useLanguage();
  const [activeSolution, setActiveSolution] = useState<string>("wellhead-sand");
  const currentSolution = solutionsList.find(s => s.id === activeSolution) || solutionsList[0];
  return <section id="oilgassolutions" data-nav="light" className="bg-muted/50 text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Header Block */}
        <Reveal>
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-muted text-foreground mf-caption font-semibold tracking-wide uppercase border border-border">
              <Activity className="size-3.5 text-primary" />
              <span>{pick(lang, copy.badgeCategory)}</span>
            </div>

            <h2 className="mf-h2 text-foreground font-sans">
              {pick(lang, copy.h2)}
            </h2>

            <p className="mf-lead text-muted-foreground">
              {pick(lang, copy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Interactive Desktop Tab / Mobile List Layout */}
        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Navigation Column */}
          <Reveal className="lg:col-span-5 flex flex-col gap-3">
            <span className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground px-1">
              {pick(lang, copy.selectApp)}
            </span>
            {solutionsList.map((sol, index) => {
            const IconComp = sol.icon;
            const isActive = sol.id === activeSolution;
            return <Button key={sol.id} data-index={index} variant="ghost" onClick={() => setActiveSolution(sol.id)} className={`h-auto w-full justify-start text-left p-4 rounded-lg border transition-colors flex items-start gap-4 cursor-pointer whitespace-normal font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? "bg-card border-primary text-card-foreground" : "bg-muted/40 border-border text-muted-foreground hover:bg-muted/80 hover:text-foreground"}`}>
                  <div className={`p-2.5 rounded-md shrink-0 transition-colors ${isActive ? "bg-primary text-primary-foreground" : "bg-background border border-border text-muted-foreground"}`}>
                    <IconComp className="size-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="mf-caption font-medium text-muted-foreground uppercase tracking-wider">
                        {pick(lang, sol.category)}
                      </span>
                      {isActive && <ChevronRight className="size-4 text-primary shrink-0" />}
                    </div>
                    <h3 className="font-semibold text-foreground text-sm sm:text-base mt-0.5 truncate">
                      {pick(lang, sol.title)}
                    </h3>
                  </div>
                </Button>;
          })}
          </Reveal>

          {/* Solution Detail Panel */}
          <Reveal delay={120} className="lg:col-span-7">
            <Card className="border-border bg-card rounded-lg overflow-hidden">
              <CardHeader className="border-b border-border bg-muted/20 pb-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <Badge variant="outline" className="bg-muted text-foreground font-mono mf-caption border border-border">
                    {pick(lang, currentSolution.category)}
                  </Badge>
                  <span className="mf-caption font-mono text-muted-foreground">
                    {pick(lang, copy.apertureRangeLabel)} {currentSolution.apertureRange}
                  </span>
                </div>
                <CardTitle className="mf-h3 text-foreground">
                  {pick(lang, currentSolution.title)}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm sm:text-base mt-2 leading-relaxed">
                  {pick(lang, currentSolution.description)}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6 pt-6">
                {/* Visual Representation Graphic Banner */}
                <div className="mf-media-framed mf-ratio-card flex items-center justify-center p-6 relative">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/oil_gas_filtration_mesh.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/oil_gas_filtration_mesh.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/oil_gas_filtration_mesh.webp-wvc-sizes" alt={pick(lang, copy.imgAlt)} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-surface-dark/75" />
                  <div className="relative z-10 text-surface-dark-foreground w-full flex items-end justify-between">
                    <div>
                      <p className="mf-caption font-mono text-surface-dark-subtle uppercase">
                        {pick(lang, copy.materialGradesLabel)}
                      </p>
                      <p className="text-sm font-semibold text-surface-dark-foreground mt-0.5">
                        {currentSolution.alloys.join(" • ")}
                      </p>
                    </div>
                    <Sliders className="size-5 text-surface-dark-subtle shrink-0" />
                  </div>
                </div>

                {/* Key Benefit Banner */}
                <div className="p-4 rounded-md bg-muted/60 border border-border flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="mf-caption font-semibold text-foreground uppercase tracking-wider">
                      {pick(lang, copy.engineeringAdvantage)}
                    </h4>
                    <p className="text-sm font-medium text-foreground mt-0.5">
                      {pick(lang, currentSolution.keyBenefit)}
                    </p>
                  </div>
                </div>

                {/* Specifications Metrics Grid */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  {currentSolution.metrics.map((metric, i) => <div key={i} data-index={i} className="p-3 rounded-md border border-border bg-background">
                      <div className="mf-caption text-muted-foreground font-medium">
                        {pick(lang, metric.label)}
                      </div>
                      <div className="text-base font-semibold text-foreground mt-1 font-mono">
                        {metric.value}
                      </div>
                    </div>)}
                </div>
              </CardContent>

              <CardFooter className="border-t border-border bg-muted/10 pt-4 pb-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2 mf-caption text-muted-foreground">
                  <Gauge className="size-4 text-primary" />
                  <span>{pick(lang, copy.customSizesNote)}</span>
                </div>
                <Button size="sm" asChild>
                  <Link to="/RequestAQuotePage">
                    <span>{pick(lang, copy.requestSizing)}</span>
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </Reveal>
        </div>

        {/* Technical Quality & Sizing Features Bar */}
        <div className="mt-16 pt-12 border-t border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {technicalSpecs.map((spec, i) => <Reveal key={i} delay={i * 60}>
              <div data-index={i} className="p-5 rounded-lg bg-card border border-border hover:border-primary/40 transition-colors h-full">
                <h3 className="font-semibold text-foreground text-sm sm:text-base">
                  {pick(lang, spec.title)}
                </h3>
                <p className="mf-caption text-muted-foreground mt-2 leading-relaxed">
                  {pick(lang, spec.desc)}
                </p>
              </div>
            </Reveal>)}
        </div>

        {/* Main CTA Callout Card */}
        <Reveal delay={180}>
          <div className="mt-12 rounded-lg bg-surface-dark text-surface-dark-foreground p-8 sm:p-10 border border-surface-dark-border">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="max-w-2xl space-y-2">
                <span className="mf-caption font-mono uppercase tracking-widest text-surface-dark-subtle">
                  {pick(lang, copy.ctaEyebrow)}
                </span>
                <h3 className="mf-h3 text-surface-dark-foreground">
                  {pick(lang, copy.ctaTitle)}
                </h3>
                <p className="text-sm sm:text-base text-surface-dark-muted leading-relaxed">
                  {pick(lang, copy.ctaBody)}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 shrink-0">
                <Button size="lg" variant="secondary" asChild className="font-medium">
                  <Link to="/RequestAQuotePage">
                    <span>{pick(lang, copy.requestSizing)}</span>
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="bg-transparent border-surface-dark-border text-surface-dark-foreground hover:bg-surface-dark-elevated hover:text-surface-dark-foreground">
                  <Link to="/DatasheetsPage">
                    <FileText className="size-4 mr-1.5" />
                    <span>{pick(lang, copy.ctaDatasheets)}</span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}